import { Injectable } from '@angular/core';
import { doc, getDoc } from '@angular/fire/firestore';
import { FirestoreService, Params } from './firebase/firestore.service';
import { COLLECTION_NAMES } from '@libs/constants/collections.const';
import { AlertConfigs, AlertType } from '@libs/models/alert.models';
import { AppliedCoupon, Coupon, CouponValidation } from '@libs/models/coupon.models';
import { toDateObject } from '@libs/utils/date.utils';

const COUPON_EXHAUSTED_MESSAGE = 'Cupom esgotado.';
const COUPON_LIMIT_MESSAGE = 'Limite de uso do cupom atingido.';

@Injectable({
  providedIn: 'root'
})
export class CouponService extends FirestoreService {
  private readonly storageKey = 'coupon';
  private appliedCoupon: AppliedCoupon | null = this.readStoredCoupon();

  constructor() {
    super();

    this.setCollectionRef(COLLECTION_NAMES.COUPON);
  }

  loadAll(coupon: string) {
    const params: Params = {
      where: [
        {
          key: 'code', condition: '==', value: coupon
        }
      ]
    };

    return this.load(params, true);
  }

  public async validateCoupon(code: string, uid: string): Promise<CouponValidation> {
    const [coupon]: Coupon[] = await this.loadAll(code.toUpperCase()) ?? [];

    if (!coupon) {
      return {
        alert: this.buildAlert(AlertType.ERROR, 'Código Inválido!', {
          descriptionHtml: 'Código de desconto inválido. <br>Verifique e tente novamente.'
        })
      };
    }

    if (this.isExpired(coupon)) {
      return {
        alert: this.buildAlert(AlertType.ERROR, 'Código Expirado!', {
          description: 'Este código de desconto expirou.'
        })
      };
    }

    if (this.isGlobalLimitReached(coupon)) {
      return { alert: this.unavailableAlert() };
    }

    const userLimit = this.getUserLimit(coupon);

    if (userLimit !== undefined && await this.getUserUses(coupon.id ?? '', uid) >= userLimit) {
      return { alert: this.usedAlert() };
    }

    return {
      coupon,
      alert: this.buildAlert(AlertType.NONE, 'Código Válido!', {
        description: 'Código de desconto aplicado.'
      })
    };
  }

  public getLimitAlert(message?: string): AlertConfigs | null {
    if (message === COUPON_EXHAUSTED_MESSAGE) return this.unavailableAlert();
    if (message === COUPON_LIMIT_MESSAGE) return this.usedAlert();

    return null;
  }

  public get coupon(): AppliedCoupon | null {
    return this.appliedCoupon;
  }

  public saveCoupon(coupon: Coupon): void {
    this.appliedCoupon = { id: coupon.id, code: coupon.code, type: coupon.type, value: coupon.value };
    this.persistCoupon();
  }

  public clearCoupon(): void {
    this.appliedCoupon = null;
    this.persistCoupon();
  }

  private unavailableAlert(): AlertConfigs {
    return this.buildAlert(AlertType.ERROR, 'Código Indisponível!', {
      description: 'Este código de desconto não está mais disponível.'
    });
  }

  private usedAlert(): AlertConfigs {
    return this.buildAlert(AlertType.ERROR, 'Código Já Utilizado!', {
      description: 'Este código de desconto já foi utilizado.'
    });
  }

  private isGlobalLimitReached(coupon: Coupon): boolean {
    const globalLimit: unknown = coupon.global_limit;

    if (globalLimit === undefined || globalLimit === null || globalLimit === '') return false;

    const remaining = Number(globalLimit);

    return Number.isFinite(remaining) && remaining <= 0;
  }

  private getUserLimit(coupon: Coupon): number | undefined {
    const userLimit = Number(coupon.user_limit);

    return Number.isFinite(userLimit) && userLimit > 0 ? userLimit : undefined;
  }

  private async getUserUses(couponId: string, uid: string): Promise<number> {
    if (!couponId || !uid) return 0;

    try {
      const snap = await getDoc(doc(this.firestore, COLLECTION_NAMES.COUPON, couponId, COLLECTION_NAMES.USERS, uid));

      return Number(snap.data()?.['uses'] ?? 0);
    } catch (error) {
      console.error('Error loading coupon user uses:', error);

      return 0;
    }
  }

  private isExpired(coupon: Coupon): boolean {
    const dateEnd = toDateObject(coupon.date_end);

    if (!dateEnd) return false;

    const limit = new Date(dateEnd);
    limit.setHours(23, 59, 59, 999);

    return limit.getTime() < Date.now();
  }

  private buildAlert(type: AlertType, title: string, content: Pick<AlertConfigs, 'description' | 'descriptionHtml'>): AlertConfigs {
    return { type, title, ...content, confirmText: 'OK' };
  }

  private persistCoupon(): void {
    try {
      if (this.appliedCoupon) {
        localStorage.setItem(this.storageKey, JSON.stringify(this.appliedCoupon));
      } else {
        localStorage.removeItem(this.storageKey);
      }
    } catch {
      return;
    }
  }

  private readStoredCoupon(): AppliedCoupon | null {
    try {
      const coupon = JSON.parse(localStorage.getItem(this.storageKey) ?? 'null');

      return coupon?.id && Number.isFinite(coupon.value)
        ? { id: coupon.id, code: coupon.code, type: coupon.type, value: coupon.value }
        : null;
    } catch {
      return null;
    }
  }
}
